import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import './CommonStyles.css';
import './AddSellStyles.css';
import './ProductSearchStyles.css';
import { PlusCircle } from 'lucide-react';
import apiService from '../../services/api';
import AddSupplier from './AddSupplier';
import dateUtils from '../../utils/dateUtils';

const AddSalesOrder = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const editSale = location.state?.editSale || null;
  const isEditMode = !!editSale;

  // Form state tailored strictly for Sales Order (no payments)
  const [formData, setFormData] = useState({
    customer: '',
    customerId: '',
    billingAddress: '',
    shippingAddress: '',
    payTerm: '',
    payTermPeriod: 'Months',
    commissionAgent: '',
    saleDate: dateUtils.getChicagoISOString().split('T')[0],
    invoiceNo: '',
    locationId: '',
    products: [],
    shippingDetails: '',
    deliveredTo: '',
    deliveryPerson: '',
    shippingStatus: 'ordered',
    shippingCharges: '0.00',
    sellNote: '',
    staffNote: '',
    totalPayable: '0.00'
  });

  const [customers, setCustomers] = useState([]);
  const [businessLocations, setBusinessLocations] = useState([]);
  const [agents, setAgents] = useState([]);
  const [usersList, setUsersList] = useState([]);

  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);

  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [displayLimit, setDisplayLimit] = useState(50);

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    loadInitialData();
  }, []);

  // Pre-fill form data when editing an existing sales order
  useEffect(() => {
    const fetchFullOrderDetails = async () => {
      if (editSale) {
        const saleId = editSale.apiData?.id || editSale.id;
        try {
          const response = await apiService.getSale(saleId);
          const apiData = response?.data?.transaction || response?.data?.[0] || response?.data || response;

          const rawLines = apiData.sell_lines || editSale.sell_lines || editSale.products || [];
          const editProducts = rawLines.map(line => {
            const prodId = line.product_id || line.id;
            const varId = line.variation_id || line.variationId || line.id;
            const name = line.product?.name || line.variations?.name || line.product_name || line.name || `Product #${prodId}`;
            const sku = line.product?.sku || line.variations?.sub_sku || line.sku || '';
            const qty = parseFloat(line.quantity) || 1;
            const unitPrice = parseFloat(line.unit_price || line.unit_price_before_discount || line.unitPrice || line.unit_price_inc_tax || 0);
            return {
              id: prodId,
              variation_id: varId,
              name,
              sku,
              quantity: qty,
              unitPrice,
              subtotal: qty * unitPrice
            };
          });

          setFormData(prev => recalculateTotal({
            ...prev,
            customer: apiData.contact?.name || editSale.customer || editSale.customer_name || '',
            customerId: apiData.contact_id || editSale.customerId || editSale.customer_id || '',
            billingAddress: apiData.contact?.address_line_1 || editSale.billingAddress || editSale.billing_address || '',
            shippingAddress: apiData.shipping_address || editSale.shippingAddress || editSale.shipping_address || '',
            payTerm: apiData.pay_term_number || editSale.payTerm || editSale.pay_term_number || '',
            payTermPeriod: apiData.pay_term_type ? (apiData.pay_term_type.charAt(0).toUpperCase() + apiData.pay_term_type.slice(1).toLowerCase()) : (editSale.payTermPeriod || 'Months'),
            commissionAgent: apiData.commission_agent || editSale.commissionAgent || editSale.commission_agent || '',
            saleDate: apiData.transaction_date ? apiData.transaction_date.split(' ')[0] : (editSale.transaction_date ? editSale.transaction_date.split(' ')[0] : dateUtils.getChicagoISOString().split('T')[0]),
            invoiceNo: apiData.invoice_no || editSale.invoice_no || editSale.invoiceNo || '',
            locationId: String(apiData.location_id || editSale.location_id || editSale.locationId || '1'),
            products: editProducts,
            shippingDetails: apiData.shipping_details || editSale.shipping_details || editSale.shippingDetails || '',
            deliveredTo: apiData.delivered_to || editSale.deliveredTo || editSale.delivered_to || '',
            deliveryPerson: apiData.delivery_person_id || editSale.deliveryPerson || editSale.delivery_person_id || '',
            shippingStatus: apiData.shipping_status || editSale.shipping_status || editSale.shippingStatus || 'ordered',
            shippingCharges: apiData.shipping_charges || editSale.shipping_charges || editSale.shippingCharges || '0.00',
            sellNote: apiData.additional_notes || editSale.additional_notes || editSale.sellNote || '',
            staffNote: apiData.staff_note || editSale.staff_note || editSale.staffNote || '',
            totalPayable: apiData.final_total || editSale.final_total || editSale.totalPayable || '0.00'
          }));

          setCustomerSearchTerm(apiData.contact?.name || editSale.customer || editSale.customer_name || '');
        } catch (err) {
          console.error('Error loading order details for edit:', err);
        }
      }
    };

    fetchFullOrderDetails();
  }, [editSale]);

  const loadInitialData = async () => {
    try {
      const [custRes, locRes, userRes] = await Promise.all([
        apiService.getContacts({ type: 'customer' }).catch(() => null),
        apiService.getBusinessLocations().catch(() => null),
        apiService.getUsers({ per_page: -1 }).catch(() => null)
      ]);

      if (custRes) {
        const transformed = (custRes.data || custRes || []).map(c => ({
          id: c.id,
          name: c.name || 'Unknown',
          address: c.address || c.billing_address || '',
          pay_term_number: c.pay_term_number,
          pay_term_type: c.pay_term_type
        }));
        if (!transformed.find(c => c.name === 'Walk-In Customer')) {
          transformed.unshift({ id: 0, name: 'Walk-In Customer', address: '' });
        }
        setCustomers(transformed);
        setFilteredCustomers(transformed);
      }

      if (locRes) {
        const locs = locRes.data || (Array.isArray(locRes) ? locRes : []);
        setBusinessLocations(locs);
        if (locs.length > 0) {
          setFormData(prev => ({ ...prev, locationId: String(locs[0].id) }));
        }
      }

      if (userRes) {
        const users = (userRes.data || userRes || []).map(u => ({
          id: u.id,
          name: u.full_name || u.name || `${u.first_name || ''} ${u.last_name || ''}`.trim()
        }));
        setUsersList(users);
        setAgents(users);
      }

      loadProducts();
    } catch (err) {
      /* safe fallback */
    }
  };

  const loadProducts = async () => {
    try {
      setIsLoadingProducts(true);
      const res = await apiService.getProducts({ per_page: 250 });
      const list = res.data || res || [];
      setProducts(list);
      setFilteredProducts(list);
    } catch (err) {
      /* ignore */
    } finally {
      setIsLoadingProducts(false);
    }
  };

  const handleProductSearch = async (term) => {
    setProductSearchTerm(term);
    setDisplayLimit(50);
    if (!term.trim()) {
      setFilteredProducts(products);
      setShowProductDropdown(false);
      return;
    }
    const searchLower = term.toLowerCase().trim();
    const matches = products.filter(p => {
      const name = (p.product || p.name || p.product_name || p.title || p.description || '').toLowerCase();
      const sku = (p.sku || p.code || '').toLowerCase();
      return name.includes(searchLower) || sku.includes(searchLower);
    });
    setFilteredProducts(matches);
    setShowProductDropdown(true);
  };

  const handleProductSelect = (product) => {
    const unitPrice = parseFloat(
      product.unit_price ||
      product.sell_price ||
      product.price ||
      product.sell_price_inc_tax ||
      product.product_variations?.[0]?.variations?.[0]?.sell_price_inc_tax ||
      product.product_variations?.[0]?.variations?.[0]?.default_sell_price ||
      0
    );
    const existingIndex = formData.products.findIndex(p => p.id === product.id);

    if (existingIndex > -1) {
      const updated = [...formData.products];
      updated[existingIndex].quantity = (parseInt(updated[existingIndex].quantity) || 0) + 1;
      updated[existingIndex].subtotal = updated[existingIndex].quantity * updated[existingIndex].unitPrice;
      setFormData(prev => recalculateTotal({ ...prev, products: updated }));
    } else {
      const newProd = {
        id: product.id,
        variation_id: product.variation_id || (product.product_variations?.[0]?.variations?.[0]?.id) || product.id,
        name: product.product || product.name || product.product_name || 'Unknown Product',
        sku: product.sku || product.code || '',
        quantity: 1,
        unitPrice: unitPrice,
        subtotal: unitPrice
      };
      setFormData(prev => recalculateTotal({ ...prev, products: [...prev.products, newProd] }));
    }
    setProductSearchTerm('');
    setShowProductDropdown(false);
  };

  const updateProductQuantity = (productId, qty) => {
    const val = Math.max(1, parseInt(qty) || 1);
    const updated = formData.products.map(p => {
      if (p.id === productId) {
        const subtotal = val * p.unitPrice;
        return { ...p, quantity: val, subtotal };
      }
      return p;
    });
    setFormData(prev => recalculateTotal({ ...prev, products: updated }));
  };

  const updateProductUnitPrice = (productId, price) => {
    const val = Math.max(0, parseFloat(price) || 0);
    const updated = formData.products.map(p => {
      if (p.id === productId) {
        const subtotal = p.quantity * val;
        return { ...p, unitPrice: val, subtotal };
      }
      return p;
    });
    setFormData(prev => recalculateTotal({ ...prev, products: updated }));
  };

  const handleRemoveProduct = (productId) => {
    const updated = formData.products.filter(p => p.id !== productId);
    setFormData(prev => recalculateTotal({ ...prev, products: updated }));
  };

  const recalculateTotal = (data) => {
    const prodTotal = data.products.reduce((sum, p) => sum + (parseFloat(p.subtotal) || 0), 0);
    const ship = parseFloat(data.shippingCharges) || 0;
    const finalTotal = (prodTotal + ship).toFixed(2);
    return { ...data, totalPayable: finalTotal };
  };

  const handleCustomerSelect = (customer) => {
    setFormData(prev => ({
      ...prev,
      customer: customer.name,
      customerId: customer.id,
      billingAddress: customer.address || '',
      shippingAddress: customer.address || '',
      payTerm: customer.pay_term_number || prev.payTerm || '',
      payTermPeriod: customer.pay_term_type ? (customer.pay_term_type.charAt(0).toUpperCase() + customer.pay_term_type.slice(1).toLowerCase()) : 'Months'
    }));
    setCustomerSearchTerm(customer.name);
    setShowCustomerDropdown(false);
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    if (!formData.customer || !formData.customerId) {
      toast.error('Please select a customer');
      return;
    }
    if (formData.products.length === 0) {
      toast.error('Please add at least one product');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        contact_id: parseInt(formData.customerId),
        location_id: parseInt(formData.locationId),
        invoice_no: formData.invoiceNo || null,
        status: 'ordered',
        type: 'sales_order',
        sale_type: 'sales_order',
        is_direct_sale: 1,
        final_total: parseFloat(formData.totalPayable),
        sale_date: dateUtils.formatUTCForApi(formData.saleDate),
        transaction_date: dateUtils.formatUTCForApi(formData.saleDate),
        shipping_charges: parseFloat(formData.shippingCharges || 0),
        shipping_details: formData.shippingDetails || null,
        shipping_address: formData.shippingAddress || null,
        shipping_status: formData.shippingStatus || 'ordered',
        delivered_to: formData.deliveredTo || null,
        delivery_person_id: formData.deliveryPerson ? parseInt(formData.deliveryPerson) : null,
        sale_note: formData.sellNote || null,
        staff_note: formData.staffNote || null,
        products: formData.products.map(p => ({
          product_id: parseInt(p.id),
          variation_id: parseInt(p.variation_id),
          quantity: parseFloat(p.quantity),
          unit_price: parseFloat(p.unitPrice)
        })),
        payments: []
      };

      if (isEditMode && editSale) {
        const saleId = editSale.apiData?.id || editSale.id;
        await apiService.updateSell(saleId, payload);
        toast.success('Sales Order updated successfully!');
      } else {
        await apiService.saveSell(payload);
        toast.success('Sales Order created successfully!');
      }
      navigate('/sell/sales-order');
    } catch (err) {
      toast.error(err.message || (isEditMode ? 'Failed to update Sales Order' : 'Failed to create Sales Order'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="sell-form-container add-sell-override" style={{ paddingBottom: '3rem' }}>
      {/* Header */}
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.25rem' }}>
        <button
          type="button"
          onClick={() => navigate('/sell/sales-order')}
          className="back-button"
          style={{ background: '#fff', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '0.4rem 0.8rem', cursor: 'pointer', fontSize: '1.1rem' }}
        >
          ←
        </button>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 700, margin: 0, color: 'var(--etpos-text, #0f172a)' }}>
          {isEditMode
            ? `Edit Sales Order (Status: ${formData.shippingStatus ? formData.shippingStatus.charAt(0).toUpperCase() + formData.shippingStatus.slice(1) : 'Ordered'})`
            : 'Add Sales Order'
          }
        </h2>
      </div>

      <form onSubmit={handleSubmit} className="sell-form">
        {/* Customer & Main Details Section */}
        <div className="form-section-content">
          <div className="form-row">
            <div className="form-group" style={{ position: 'relative' }}>
              <label>Customer:*</label>
              <div className="product-search-wrapper" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <div style={{ position: 'relative', flex: 1 }}>
                  <input
                    type="text"
                    placeholder="Enter Customer name / phone"
                    value={customerSearchTerm}
                    onChange={e => {
                      setCustomerSearchTerm(e.target.value);
                      const match = customers.filter(c => c.name.toLowerCase().includes(e.target.value.toLowerCase()));
                      setFilteredCustomers(match);
                      setShowCustomerDropdown(true);
                    }}
                    onFocus={() => setShowCustomerDropdown(true)}
                    className="form-input"
                  />
                  {showCustomerDropdown && (
                    <div className="customer-dropdown" style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20, background: '#fff', border: '1px solid #cbd5e1', borderRadius: '6px', maxHeight: '180px', overflowY: 'auto' }}>
                      {filteredCustomers.map(c => (
                        <div
                          key={c.id}
                          className="customer-dropdown-item"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleCustomerSelect(c);
                          }}
                        >
                          <div className="customer-name">{c.name}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className="add-customer-quick-btn"
                  onClick={() => setShowAddCustomerModal(true)}
                  title="Add New Customer"
                >
                  <PlusCircle size={22} color="white" fill="#3b82f6" style={{ borderRadius: '50%' }} />
                </button>
              </div>
            </div>

            <div className="form-group">
              <label>Billing Address:</label>
              <input type="text" value={formData.billingAddress} readOnly className="form-input" style={{ background: '#f8fafc' }} />
            </div>

            <div className="form-group">
              <label>Shipping Address:</label>
              <input type="text" value={formData.shippingAddress} onChange={e => setFormData({ ...formData, shippingAddress: e.target.value })} className="form-input" />
            </div>

            <div className="form-group">
              <label>Pay term:</label>
              <div className="payterm-input-group">
                <input type="number" value={formData.payTerm} onChange={e => setFormData({ ...formData, payTerm: e.target.value })} className="form-input payterm-input" />
                <select value={formData.payTermPeriod} onChange={e => setFormData({ ...formData, payTermPeriod: e.target.value })} className="form-select payterm-select">
                  <option value="Months">Mon</option>
                  <option value="Days">Day</option>
                </select>
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Sale Date:*</label>
              <input type="date" value={formData.saleDate} onChange={e => setFormData({ ...formData, saleDate: e.target.value })} className="form-input" />
            </div>

            <div className="form-group">
              <label>Business Location:*</label>
              <select value={formData.locationId} onChange={e => setFormData({ ...formData, locationId: e.target.value })} className="form-select">
                {businessLocations.map(l => (
                  <option key={l.id} value={l.id}>{l.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Order No.:</label>
              <input type="text" placeholder="Keep blank to auto-generate" value={formData.invoiceNo} onChange={e => setFormData({ ...formData, invoiceNo: e.target.value })} className="form-input" />
            </div>
          </div>
        </div>

        <hr className="section-divider" />

        {/* Product Information Section (Exact match to AddSale layout) */}
        <div className="form-section-content">
          <h3>Product Information</h3>
          <div className="form-row">
            <div className="form-group full-width">
              <label>Enter Product name / SKU / Scan bar code</label>
              <div className="product-search-wrapper" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <div className="product-dropdown-container" style={{ position: 'relative', flex: 1 }}>
                  <input
                    type="text"
                    value={productSearchTerm}
                    onChange={(e) => handleProductSearch(e.target.value)}
                    onFocus={() => setShowProductDropdown(true)}
                    className="form-input"
                    placeholder="Search products by name or SKU..."
                    autoComplete="off"
                  />
                  {isLoadingProducts && <div className="product-loading">Loading products...</div>}
                  {showProductDropdown && !isLoadingProducts && (
                    <div className="product-dropdown">
                      {filteredProducts.length > 0 ? (
                        <>
                          {filteredProducts.slice(0, displayLimit).map((product) => (
                            <div
                              key={product.id}
                              className="product-dropdown-item"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                handleProductSelect(product);
                              }}
                            >
                              <div className="product-name">
                                {product.product || product.name || product.product_name || 'Unknown Product'}
                                {(product.sku || product.code) && <span className="product-sku-inline"> • SKU: {product.sku || product.code}</span>}
                              </div>
                              <div className="product-details">
                                <span className="product-price">${parseFloat(product.sell_price || product.unit_price || 0).toFixed(2)}</span>
                              </div>
                            </div>
                          ))}
                        </>
                      ) : (
                        <div className="product-dropdown-item no-results">No products found</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="product-table" style={{ overflowX: 'auto', margin: '1rem 0' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                  <th style={{ padding: '10px 12px', textAlign: 'left', width: '35%' }}>Product</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', width: '25%' }}>Quantity</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', width: '18%' }}>Unit Price</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', width: '16%' }}>Subtotal</th>
                  <th style={{ padding: '10px 8px', textAlign: 'center', width: '6%' }}></th>
                </tr>
              </thead>
              <tbody>
                {formData.products.length > 0 ? (
                  formData.products.map((product) => (
                    <tr key={product.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '10px 12px' }}>
                        <div className="product-info-cell">
                          <div className="product-name" style={{ fontWeight: '700', color: '#1e293b', fontSize: '0.875rem' }}>{product.name}</div>
                          {product.sku && <div className="product-sku" style={{ color: '#94a3b8', fontSize: '0.75rem', marginTop: '2px' }}>{product.sku}</div>}
                        </div>
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'nowrap' }}>
                          <div style={{ display: 'inline-flex', alignItems: 'center', border: '1px solid #cbd5e1', borderRadius: '6px', overflow: 'hidden', background: '#fff' }}>
                            <button
                              type="button"
                              onClick={() => updateProductQuantity(product.id, Math.max(1, (parseInt(product.quantity) || 0) - 1))}
                              style={{ background: '#fff', color: '#ef4444', border: 'none', borderRight: '1px solid #e2e8f0', width: '26px', height: '30px', cursor: 'pointer', fontWeight: 'bold', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                              -
                            </button>
                            <input
                              type="number"
                              value={product.quantity}
                              onChange={(e) => updateProductQuantity(product.id, e.target.value)}
                              style={{ width: '38px', height: '30px', textAlign: 'center', border: 'none', outline: 'none', fontSize: '0.875rem', fontWeight: '600', color: '#1e293b' }}
                              min="1"
                            />
                            <button
                              type="button"
                              onClick={() => updateProductQuantity(product.id, (parseInt(product.quantity) || 0) + 1)}
                              style={{ background: '#fff', color: '#22c55e', border: 'none', borderLeft: '1px solid #e2e8f0', width: '26px', height: '30px', cursor: 'pointer', fontWeight: 'bold', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                              +
                            </button>
                          </div>
                          <select className="unit-select" style={{ padding: '4px 8px', fontSize: '0.8rem', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#fff', height: '32px' }}>
                            <option value="Single">Single</option>
                          </select>
                        </div>
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <input
                          type="number"
                          step="0.01"
                          className="form-input unit-price-input"
                          value={product.unitPrice}
                          onChange={(e) => updateProductUnitPrice(product.id, e.target.value)}
                          style={{ width: '100%', maxWidth: '90px', padding: '5px 8px', fontSize: '0.875rem', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#fff' }}
                        />
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <span style={{ fontWeight: '700', color: '#0f172a', fontSize: '0.875rem' }}>${product.subtotal.toFixed(2)}</span>
                      </td>
                      <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                        <button
                          type="button"
                          onClick={() => handleRemoveProduct(product.id)}
                          style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontWeight: 'bold', fontSize: '1rem' }}
                        >
                          ✕
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
                      No products added yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="product-summary" style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem 1.25rem', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px', fontWeight: 600, color: '#334155', marginTop: '0.5rem' }}>
            <span>Items: {formData.products.length}</span>
            <span>Total: ${formData.products.reduce((sum, product) => sum + (parseFloat(product.subtotal) || 0), 0).toFixed(2)}</span>
          </div>
        </div>

        <hr className="section-divider" />

        {/* Shipping & Bottom Actions Section */}
        <div className="form-section-content">
          <div className="form-row">
            <div className="form-group">
              <label>Shipping Details:</label>
              <textarea value={formData.shippingDetails} onChange={e => setFormData({ ...formData, shippingDetails: e.target.value })} className="form-input" rows="2" />
            </div>

            <div className="form-group">
              <label>Shipping Charges:</label>
              <input type="number" step="0.01" value={formData.shippingCharges} onChange={e => setFormData(recalculateTotal({ ...formData, shippingCharges: e.target.value }))} className="form-input" />
            </div>

            <div className="form-group">
              <label>Shipping Status:</label>
              <select value={formData.shippingStatus} onChange={e => setFormData({ ...formData, shippingStatus: e.target.value })} className="form-select">
                <option value="ordered">Ordered</option>
                <option value="packed">Packed</option>
                <option value="shipped">Shipped</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            <div className="form-group">
              <label>Shipping Documents:</label>
              <input type="file" multiple className="form-input" style={{ padding: '0.35rem' }} />
              <small style={{ color: '#64748b', fontSize: '0.75rem', marginTop: '4px', display: 'block' }}>Max size: 5MB (PDF, JPG, PNG...)</small>
            </div>
          </div>

          {/* Bottom Action Footer with Total Payable on right and centered Save Sales Order button */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid #e2e8f0' }}>
            <div style={{ textAlign: 'right', fontSize: '1.15rem', fontWeight: 500, color: 'var(--etpos-text, #0f172a)' }}>
              Total Payable: ${formData.totalPayable}
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
              <button
                type="submit"
                disabled={isSubmitting}
                style={{
                  padding: '0.65rem 2.5rem',
                  background: '#4f46e5',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  boxShadow: '0 2px 4px rgba(79, 70, 229, 0.2)',
                  transition: 'all 0.2s ease'
                }}
              >
                {isSubmitting
                  ? (isEditMode ? 'Updating Sales Order...' : 'Saving Sales Order...')
                  : (isEditMode ? 'Update Sales Order' : 'Save Sales Order')
                }
              </button>
            </div>
          </div>
        </div>
      </form>

      {showAddCustomerModal && (
        <AddSupplier
          initialType="customers"
          onSuccess={(newCust) => {
            if (newCust) {
              handleCustomerSelect({ id: newCust.id, name: newCust.name || `${newCust.first_name || ''} ${newCust.last_name || ''}`.trim(), address: newCust.address || newCust.address_line_1 || '' });
            }
            setShowAddCustomerModal(false);
          }}
        />
      )}
    </div>
  );
};

export default AddSalesOrder;
